import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, X, Loader2, CalendarDays, Tag, HardHat, StickyNote } from "lucide-react";
import { api, API } from "../../lib/api";

function fmtTs(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; }
}
const CAT_LABEL = { avanzamento: "Avanzamento", contestazione: "Contestazione", consegna: "Consegna", altro: "Altro" };

export default function InstallerRecentPhotosBar() {
  const [photos, setPhotos] = useState([]);
  const [gallery, setGallery] = useState(null); // { loading, meta } | { meta, photos }
  const [zoom, setZoom] = useState(null);
  const token = localStorage.getItem("ah_token") || "";
  const src = (p) => `${API}${(p.url || "").replace("/api", "")}?token=${token}`;
  const mounted = useRef(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/installers/admin/photos/recent", { params: { limit: 10 } });
      if (mounted.current) setPhotos(data.photos || []);
      // Segna l'attività come vista finché l'admin è sulla dashboard (azzera il badge INSTALLERS)
      api.post("/installers/admin/activity/seen").catch(() => {});
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    const t = setInterval(load, 20000);
    return () => { mounted.current = false; clearInterval(t); };
  }, [load]);

  useEffect(() => {
    if (!gallery && !zoom) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (zoom) setZoom(null);
      else if (gallery) setGallery(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gallery, zoom]);

  async function openGallery(p) {
    setGallery({ loading: true, meta: p });
    try {
      const { data } = await api.get("/installers/photos", { params: { task_id: p.task_id } });
      setGallery({ meta: p, photos: data.photos || [] });
    } catch {
      setGallery({ meta: p, photos: [] });
    }
  }

  if (photos.length === 0) return null;

  return (
    <div className="mb-4" data-testid="installers-recent-photos-bar">
      <div className="flex items-center gap-2 mb-2 text-[10px] tracking-[0.18em] uppercase text-slate1">
        <Camera size={13} /> Ultime foto scattate
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {photos.slice(0, 10).map((p) => (
          <button
            key={p.id}
            onClick={() => openGallery(p)}
            className="relative shrink-0 w-[104px] h-[104px] rounded-sm overflow-hidden border border-ink/10 bg-cream2 group"
            title={`${p.client_name || p.title || ""} · ${fmtTs(p.uploaded_at)}`}
            data-testid={`recent-photo-${p.id}`}
          >
            <img src={src(p)} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
            <span className="absolute inset-x-0 bottom-0 bg-ink/70 text-cream text-[9px] px-1.5 py-1 truncate text-left leading-tight">
              {p.client_name || p.title || "—"}
            </span>
            {p.category === "contestazione" && (
              <span className="absolute top-1 left-1 bg-rose-600 text-white text-[8px] tracking-wider uppercase font-bold px-1 py-0.5 rounded-sm">Contest.</span>
            )}
          </button>
        ))}
      </div>

      {/* Galleria foto della consegna */}
      {gallery && (
        <div className="fixed inset-0 z-[9500] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4" data-testid="recent-gallery-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) setGallery(null); }}>
          <div className="bg-cream w-[min(96vw,860px)] max-h-[88vh] flex flex-col border border-ink/15 shadow-2xl rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white px-6 py-4 border-b border-ink/10 flex items-start justify-between gap-3 shrink-0">
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.18em] uppercase text-slate1">Foto consegna</div>
                <div className="font-display text-lg text-ink leading-tight truncate">{gallery.meta.client_name || gallery.meta.title || "—"}</div>
                <div className="text-xs text-slate1 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  <span className="inline-flex items-center gap-1"><CalendarDays size={11} /> {fmtDate(gallery.meta.scheduled_date)}</span>
                  {gallery.meta.riferimento && <span className="inline-flex items-center gap-1"><Tag size={11} /> Rif. {gallery.meta.riferimento}</span>}
                </div>
              </div>
              <button onClick={() => setGallery(null)} className="p-1.5 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5 shrink-0" data-testid="recent-gallery-close"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto p-4">
              {gallery.loading ? (
                <div className="flex justify-center py-12 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
              ) : gallery.photos.length === 0 ? (
                <div className="text-center py-12 text-sm text-slate-400" data-testid="recent-gallery-empty">Nessuna foto per questa consegna</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {gallery.photos.map((ph) => (
                    <figure key={ph.id} className="bg-cream2 border border-ink/10 rounded-sm overflow-hidden" data-testid={`recent-gallery-photo-${ph.id}`}>
                      <div className="relative aspect-[4/3] cursor-zoom-in" onClick={() => setZoom(src(ph))}>
                        <img src={src(ph)} alt="" loading="lazy" className="w-full h-full object-cover" />
                        {ph.category && <span className="absolute top-1 left-1 bg-ink/70 text-cream text-[8px] tracking-wider uppercase px-1 py-0.5 rounded-sm">{CAT_LABEL[ph.category] || ph.category}</span>}
                      </div>
                      <figcaption className="p-2 text-[11px] text-slate-600 space-y-1">
                        <div className="flex items-center gap-1 truncate"><HardHat size={10} className="text-slate-400 shrink-0" /> <span className="truncate">{ph.worker_name || "—"}</span> <span className="text-slate-400 ml-auto shrink-0">{fmtTs(ph.uploaded_at)}</span></div>
                        {ph.comment && <div className="flex items-start gap-1 text-amber-800"><StickyNote size={10} className="text-amber-500 shrink-0 mt-0.5" /> <span className="line-clamp-2">{ph.comment}</span></div>}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {zoom && (
        <div className="fixed inset-0 z-[9600] flex items-center justify-center bg-ink/85 p-4" data-testid="recent-zoom" onClick={() => setZoom(null)}>
          <button onClick={() => setZoom(null)} className="absolute top-4 right-4 p-2 text-cream hover:text-white"><X size={22} /></button>
          <img src={zoom} alt="" className="max-w-[92vw] max-h-[88vh] object-contain rounded-sm" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
